import { Box, Step, StepButton, Stepper, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { createSelector } from '@reduxjs/toolkit'
import React, { useContext, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { Link, useLocation } from 'react-router-dom'
import ErrorAlert from '../../Layouts/ErrorAlert'
import { AuthContext } from '../../libs/contexts/authContext'
import { filterNonDigits } from '../../libs/utils'
import { createAccount } from '../../store/actions/authActions'
import FirstRegisterStep from './FirstRegisterStep'
import SecondRegisterStep from './SecondRegisterStep'
import ThirdRegisterStep from './ThirdRegisterStep'
import { selectMsgText, selectMsgTitle } from './utils'

const StyledLink = styled(Link)({
  color: 'blue',
  textDecoration: 'none',

  '&:focus, &:hover, &:visited, &:link, &:active': {
    color: 'blue',
  },
})

export const selectNecessaryProps = createSelector([selectMsgTitle, selectMsgText], (msgTitle, msgText) => ({
  msgTitle,
  msgText,
}))

function RegisterPage() {
  const { search } = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { affiliateCode, invalidUrl, urlErrorMessage, productId } = useContext(AuthContext)
  const { msgTitle, msgText } = useSelector(selectNecessaryProps)
  const [sameAddress, setSameAddress] = useState(false)
  const [registrationForm, setRegistrationForm] = useState({
    workEmail: '',
    password: '',
    organizationName: '',
    workAgentPhone: '',
    profilePhoto: null,
    firstName: '',
    lastName: '',
    personalEmail: '',
    personalPhone: '',
    personalWebsite: '',
    isSameAddress: false,
    agent_id: '',
  })
  const [personalAddress, setPersonalAddress] = useState({
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
  })
  const [workAddress, setWorkAddress] = useState({
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
  })
  const [currStep, setCurrStep] = useState(0)
  const [showError, setShowError] = useState(false)

  const handleRegistrationFormChange = ({ target: { files, name, value } }) => {
    if (name === 'profilePhoto') {
      const file = files[0]
      if (file && (file.type === 'image/png' || file.type === 'image/jpeg')) {
        const fileBytes = URL.createObjectURL(file)
        setRegistrationForm({
          ...registrationForm,
          profilePhoto: fileBytes,
        })
      }
      return
    } else if (name === 'personalPhone' || name === 'workAgentPhone') {
      const phoneNumber = filterNonDigits(value)
      setRegistrationForm((prev) => ({
        ...prev,
        [name]: phoneNumber,
      }))
      return
    }
    setRegistrationForm({
      ...registrationForm,
      [name]: value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    // const formInput = {
    //   ...registrationForm,
    //   agentPhoto: registrationForm.profilePhoto,
    //   login: registrationForm.workEmail,
    //   agentPersonalWeb: registrationForm.personalEmail,
    //   homeAddress1: personalAddress.address1,
    //   homeAddress2: personalAddress.address2,
    //   homeCity: personalAddress.city,
    //   homeState: personalAddress.state,
    //   // isSameAddress: registrationForm.isSameAddress,
    // }
    const formInput = {
      login: registrationForm.workEmail,
      agent_id: registrationForm.agent_id,
      password: registrationForm.password,
      workAgentPhone: registrationForm.workAgentPhone,
      workZipCode: workAddress.zipCode,
      workAddress1: workAddress.address1,
      workAddress2: workAddress.address2,
      workCity: workAddress.city,
      workState: workAddress.state,
      firstName: registrationForm.firstName,
      lastName: registrationForm.lastName,
      personalEmailAddess: registrationForm.personalEmail,
      personalPhone: registrationForm.personalPhone,
      homeZipCode: personalAddress.zipCode,
      homeAddress1: personalAddress.address1,
      homeAddress2: personalAddress.address2,
      homeCity: personalAddress.city,
      homeState: personalAddress.state,
      agentPersonalWeb: registrationForm.personalWebsite,
      agentPhoto: registrationForm.profilePhoto,
      isSameAddress: sameAddress,
      productId: productId === '81403' ? 81403 : 81401,
    }
    console.log(formInput)
    const res = await dispatch(createAccount(formInput))
    if (res.statusCode === 201) {
      setShowError(true)
    }
    if (res && res.accountCreated) {
      setShowError(false)
      navigate(`/success?affiliateCode=${affiliateCode}`)
    } else {
      setShowError(true)
    }
  }

  const handleNext = () => {
    setCurrStep(currStep + 1)
  }

  const handleSteps = () => {
    switch (currStep) {
      case 0:
        return (
          <FirstRegisterStep
            registrationForm={registrationForm}
            handleRegistrationFormChange={handleRegistrationFormChange}
            setRegistrationForm={setRegistrationForm}
            workAddress={workAddress}
            setWorkAddress={setWorkAddress}
            handleNext={handleNext}
          />
        )
      case 1:
        return (
          <SecondRegisterStep
            registrationForm={registrationForm}
            setRegistrationForm={setRegistrationForm}
            handleRegistrationFormChange={handleRegistrationFormChange}
            workAddress={workAddress}
            setWorkAddress={setWorkAddress}
            handleNext={handleNext}
          />
        )
      case 2:
        return (
          <ThirdRegisterStep
            registrationForm={registrationForm}
            setRegistrationForm={setRegistrationForm}
            handleRegistrationFormChange={handleRegistrationFormChange}
            personalAddress={personalAddress}
            workAddress={workAddress}
            setPersonalAddress={setPersonalAddress}
            sameAddress={sameAddress}
            setSameAddress={setSameAddress}
            handleSubmit={handleSubmit}
          />
        )
      default:
        throw new Error(`Invalid step: ${currStep}`)
    }
  }

  return (
    <>
      <ErrorAlert
        showError={invalidUrl || showError}
        setShowError={setShowError}
        msgTitle={msgTitle}
        messageText={urlErrorMessage || msgText}
      />
      <Box sx={registerStyle.containerBox}>
        <Typography variant="h4" align="center" sx={{ mt: '6px', mb: '6px' }}>
          Create an Account
        </Typography>
        <Typography variant="subtitle2" align="center" sx={{ mt: '6px', fontFamily: 'Roboto !important' }}>
          Register to access Life Insurance Applications Portal products and manage your applications.
        </Typography>
        <Stepper activeStep={currStep} alternativeLabel sx={{ my: { sm: '40px', xs: '20px' }, width: '90%' }}>
          <Step>
            <StepButton onClick={() => setCurrStep(0)}>Login Info</StepButton>
          </Step>
          <Step>
            <StepButton onClick={() => currStep >= 1 && setCurrStep(1)}>About your Company</StepButton>
          </Step>
          <Step>
            <StepButton onClick={() => currStep > 1 && setCurrStep(2)}>About You</StepButton>
          </Step>
        </Stepper>

        {handleSteps()}
        <Box sx={{ mt: '32px', textAlign: 'center' }}>
          <Typography variant="body2" sx={{ fontSize: '12px' }}>
            If you already have an account please <StyledLink to={{ pathname: '/login', search }}>login</StyledLink>
          </Typography>
        </Box>
      </Box>
    </>
  )
}

export default RegisterPage

const registerStyle = {
  containerBox: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: { lg: '626px', md: '770px', sm: '770px', xs: '343px' },
    height: 'auto',
    background: '#F5F7FF',
    margin: '100px auto',
    padding: { lg: '64px 52px', md: '40px 32px ', sm: '40px 32x', xs: '16px' },
  },
}
